import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import subscriptionService from '../../services/subscription-service';
import freeTrialService from '../../services/free-trial-service';
import './CustomerSubscriptionDashboard.scss';

const CustomerSubscriptionDashboard = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubscription, setExpandedSubscription] = useState(null);
  const [products, setProducts] = useState({});
  const [trialStatus, setTrialStatus] = useState(null);
  const [swapModal, setSwapModal] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [swapping, setSwapping] = useState(false);
  const [offerSettings, setOfferSettings] = useState(null);
  const [showOfferAlert, setShowOfferAlert] = useState(true);
  const customerId = sessionStorage.getItem('customerId');
  const navigate = useNavigate();

  useEffect(() => {
    loadSubscriptions();
    loadTrialStatus();
    loadOfferSettings();

    // If navigated here right after trial claim, do a delayed re-fetch
    // to allow the backend time to finish writing the subscription doc
    const timer = setTimeout(() => {
      loadSubscriptions();
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleRefreshSubscriptions = () => {
      loadSubscriptions();
    };
    window.addEventListener('refreshSubscriptions', handleRefreshSubscriptions);
    return () => window.removeEventListener('refreshSubscriptions', handleRefreshSubscriptions);
  }, []);

  const loadTrialStatus = async () => {
    if (sessionStorage.getItem('trialJustClaimed') === 'true') {
      setTrialStatus({ eligible: false, claimed: true });
      return;
    }
    const s = await freeTrialService.getTrialStatus();
    setTrialStatus(s);
    if (s.claimed === true) {
      sessionStorage.setItem('trialJustClaimed', 'true');
    }
  };

  const loadOfferSettings = async () => {
    try {
      const data = await subscriptionService.getSubscriptionPlans();
      if (data.length > 0 && data[0].offerSettings) {
        setOfferSettings(data[0].offerSettings);
      }
    } catch (error) {
      console.error('Error loading offer settings:', error);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const data = await subscriptionService.getUserSubscriptions(customerId);
      setSubscriptions(data);
      if (data.length > 0) {
        const productService = (await import('../../db/product-service')).default;
        const allProducts = await productService.getAllProducts();
        const productMap = {};
        allProducts.forEach(p => { productMap[p._id] = p; });
        setProducts(productMap);
        // Auto-expand the trial subscription if it exists
        const trial = data.find(s => s.isTrial || s.planType === 'free_trial');
        if (trial) setExpandedSubscription(trial._id);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
    setLoading(false);
  };

  const getDeliverySchedule = (subscription) => {
    if (!subscription.deliverySchedule || Object.keys(subscription.deliverySchedule).length === 0) {
      return [];
    }
    const schedule = [];
    Object.entries(subscription.deliverySchedule).forEach(([dateKey, day]) => {
      const deliveryDate = new Date(dateKey);
      schedule.push({
        date: dateKey,
        displayDate: deliveryDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        status: day.status || 'pending',
        shot: day.shot,
        smoothie: day.smoothie
      });
    });
    return schedule.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getProductName = (productId) => {
    return products[productId]?.name || 'Product';
  };

  const getScheduleStats = (schedule) => {
    const delivered = schedule.filter(d => d.status === 'delivered').length;
    const dispatched = schedule.filter(d => d.status === 'dispatched').length;
    const pending = schedule.filter(d => d.status === 'pending').length;
    return { delivered, dispatched, pending, total: schedule.length };
  };

  const pauseSubscription = async (subscriptionId) => {
    try {
      await subscriptionService.updateSubscriptionStatus(subscriptionId, 'paused');
      await loadSubscriptions();
    } catch (error) {
      console.error('Error pausing subscription:', error);
      alert('Error pausing subscription');
    }
  };

  const resumeSubscription = async (subscriptionId) => {
    try {
      await subscriptionService.updateSubscriptionStatus(subscriptionId, 'active');
      await loadSubscriptions();
    } catch (error) {
      console.error('Error resuming subscription:', error);
      alert('Error resuming subscription');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'paused': return '#ffc107';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const canSwapDelivery = (dateKey, status) => {
    if (status !== 'pending') return false;
    const deliveryDate = new Date(dateKey);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    deliveryDate.setHours(0, 0, 0, 0);
    return deliveryDate >= tomorrow;
  };

  const openSwapModal = async (subscription, dateKey, delivery) => {
    try {
      const productType = delivery.shot ? 'shot' : 'smoothie';
      const currentProductId = delivery[productType];
      const productService = (await import('../../db/product-service')).default;
      const allProducts = await productService.getAllProducts();
      let filtered = [];
      if (productType === 'shot') {
        filtered = allProducts.filter(p => 
          p.category === 'shot' || p.type === 'shot' || p.name?.toLowerCase().includes('shot') || p.name?.toLowerCase().includes('juice')
        );
      } else {
        filtered = allProducts.filter(p => 
          p.category === 'smoothie' || p.type === 'smoothie' || p.name?.toLowerCase().includes('smoothie')
        );
      }
      setAvailableProducts(filtered);
      setSelectedProduct(currentProductId);
      setSwapModal({ subscription, dateKey, delivery, productType });
    } catch (error) {
      console.error('Error opening swap modal:', error);
      alert('Failed to load products');
    }
  };

  const closeSwapModal = () => {
    setSwapModal(null);
    setAvailableProducts([]);
    setSelectedProduct(null);
  };

  const handleSwapProduct = async () => {
    if (!selectedProduct || !swapModal) return;
    const { subscription, dateKey, productType } = swapModal;
    const currentProductId = subscription.deliverySchedule[dateKey][productType];
    if (currentProductId === selectedProduct) {
      alert('Please select a different product');
      return;
    }
    setSwapping(true);
    try {
      await subscriptionService.swapDeliveryProduct(subscription._id, dateKey, productType, selectedProduct);
      await loadSubscriptions();
      closeSwapModal();
      alert('✅ Product swapped successfully!');
    } catch (error) {
      console.error('Error swapping product:', error);
      alert('Failed to swap product. Please try again.');
    }
    setSwapping(false);
  };

  if (loading) return <div className="loading">Loading your subscriptions...</div>;

  return (
    <div className="customer-subscription-dashboard">
      {offerSettings?.showOffer && showOfferAlert && (
        <div className="offer-alert">
          <div className="offer-alert-content">
            <div className="offer-icon">🎉</div>
            <div className="offer-text">
              <strong>{offerSettings.offerName}!</strong> Get {offerSettings.discountPercentage}% OFF on Booster Plan!
            </div>
            <button className="offer-cta" onClick={() => navigate('/subscriptions')}>
              Subscribe Now
            </button>
            <button className="offer-close" onClick={() => setShowOfferAlert(false)}>
              ×
            </button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>My Subscriptions</h2>
        <button 
          onClick={loadSubscriptions}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          🔄 Refresh
        </button>
      </div>
      
      {subscriptions.length === 0 ? (
        <div className="no-subscriptions">
          {trialStatus?.eligible && !trialStatus?.claimed && (
            <div className="trial-offer-card">
              <div className="trial-offer-icon">🎁</div>
              <h3>Welcome Gift — Try Before You Subscribe!</h3>
              <p>Get <strong>5 immunity shots</strong> for just ₹5 activation. Pick your shots and we'll schedule them for you.</p>
              <button className="claim-trial-btn" onClick={() => navigate('/subscriptions')}>
                🎁 Claim Free Trial for ₹100
              </button>
            </div>
          )}
          {trialStatus?.expired && (
            <p className="trial-expired-note">⏰ Your free trial offer has expired.</p>
          )}
          <p>You don't have any subscriptions yet.</p>
          <button className="browse-plans" onClick={() => navigate('/subscriptions')}>Browse Subscription Plans</button>
        </div>
      ) : (
        <div className="subscriptions-list">
          {subscriptions.map(subscription => (
            <div key={subscription._id} className="subscription-card">
              <div className="subscription-header">
                <h3>{subscription.planName}</h3>
                <span 
                  className="status"
                  style={{ backgroundColor: getStatusColor(subscription.status) }}
                >
                  {subscription.status.toUpperCase()}
                </span>
              </div>

              <div className="subscription-details">
                <div className="detail-row">
                  <span className="label">Subscription ID:</span>
                  <span className="value">{subscription._id}</span>
                </div>
                {subscription.isTrial && (
                  <>
                    <div className="detail-row trial-info">
                      <span className="label">🎁 Welcome Gift:</span>
                      <span className="value">5-day trial</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">📍 Pickup:</span>
                      <span className="value" style={{ color: '#2e7d32', fontWeight: '600' }}>Store Pickup Only</span>
                    </div>
                    <div style={{ margin: '10px 0', padding: '10px 14px', backgroundColor: '#e8f5e9', borderLeft: '4px solid #4CAF50', borderRadius: '4px', fontSize: '13px', color: '#2e7d32' }}>
                      🏪 <strong>Pickup Location:</strong> Opp Candeur, Bapu Nagar, Serilingampalle (M), Hyderabad – 500019<br/>
                      📞 <strong>Contact:</strong> +91 9390649371<br/>
                      🕗 <strong>Pickup Time:</strong> 8:00 AM – 9:00 AM (Mon–Fri)
                    </div>
                  </>
                )}
                {!subscription.isTrial && (
                  <>
                    <div className="detail-row">
                      <span className="label">Monthly Price:</span>
                      <span className="value">₹{subscription.monthlyPrice}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Start Date:</span>
                      <span className="value">{formatDate(subscription.startDate)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Next Billing:</span>
                      <span className="value">{formatDate(subscription.nextBillingDate)}</span>
                    </div>
                  </>
                )}
                {(() => {
                  const schedule = getDeliverySchedule(subscription);
                  const stats = getScheduleStats(schedule);
                  return (
                    <div className="detail-row">
                      <span className="label">Deliveries:</span>
                      <span className="value">
                        {stats.delivered} delivered, {stats.dispatched} dispatched, {stats.pending} pending
                      </span>
                    </div>
                  );
                })()}
              </div>

              <button 
                className="view-schedule-btn"
                onClick={() => setExpandedSubscription(expandedSubscription === subscription._id ? null : subscription._id)}
              >
                {expandedSubscription === subscription._id ? 'Hide Schedule' : 'View Delivery Schedule'}
              </button>

              {expandedSubscription === subscription._id && (() => {
                const schedule = getDeliverySchedule(subscription);
                if (schedule.length === 0) {
                  return (
                    <div className="delivery-schedule">
                      <h4>Delivery Schedule</h4>
                      <p className="no-schedule">
                        No delivery schedule found. This subscription may have been created before the scheduling feature was added.
                        <br />
                        <small style={{ color: '#666', fontSize: '12px' }}>
                          💡 Please contact support if your delivery schedule is missing.
                        </small>
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="delivery-schedule">
                    <h4>Delivery Schedule</h4>
                    <div className="schedule-grid">
                      {schedule.map((delivery, index) => {
                        const canSwap = canSwapDelivery(delivery.date, delivery.status);
                        return (
                          <div key={delivery.date} className={`delivery-item ${delivery.status}`}>
                            <div className="delivery-header">
                              <span className="delivery-number">Day {index + 1}</span>
                              <span className="delivery-date">{delivery.displayDate}</span>
                            </div>
                            <div className="delivery-status">
                              {delivery.status === 'delivered' && <span className="status-badge delivered">✓ Delivered</span>}
                              {delivery.status === 'dispatched' && <span className="status-badge dispatched">Dispatched</span>}
                              {delivery.status === 'pending' && <span className="status-badge pending">Pending</span>}
                            </div>
                            {delivery.status === 'delivered' && (
                              <a
                                href="https://g.page/r/CTxBWNoo8H1PEAE/review"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-block', marginTop: '6px', fontSize: '12px', color: '#f9a825', fontWeight: '600', textDecoration: 'none' }}
                              >
                                ⭐ Enjoyed it? Leave a Review
                              </a>
                            )}
                            <div className="delivery-products">
                              {delivery.shot && (
                                <div className="product-item shot">
                                  <span className="product-type">Shot:</span>
                                  <span className="product-name">{getProductName(delivery.shot)}</span>
                                </div>
                              )}
                              {delivery.smoothie && (
                                <div className="product-item smoothie">
                                  <span className="product-type">Smoothie:</span>
                                  <span className="product-name">{getProductName(delivery.smoothie)}</span>
                                </div>
                              )}
                            </div>
                            {canSwap && (
                              <button className="swap-product-btn" onClick={() => openSwapModal(subscription, delivery.date, delivery)}>
                                🔄 Swap Product
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div className="subscription-actions">
                {/* Pause/Resume functionality temporarily hidden */}
              </div>
            </div>
          ))}
        </div>
      )}

      {swapModal && (
        <div className="swap-modal-overlay" onClick={closeSwapModal}>
          <div className="swap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="swap-modal-header">
              <h3>🔄 Swap Product</h3>
              <button className="close-btn" onClick={closeSwapModal}>×</button>
            </div>
            <div className="swap-modal-body">
              <div className="current-product-info">
                <p className="delivery-date">
                  <strong>Delivery Date:</strong> {new Date(swapModal.dateKey).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <p className="current-product">
                  <strong>Current Product:</strong> {swapModal.productType === 'shot' ? '🥤' : '🥛'} {getProductName(swapModal.delivery[swapModal.productType])}
                </p>
              </div>
              <div className="product-selection">
                <h4>Select New Product:</h4>
                <div className="products-grid">
                  {availableProducts.map(product => (
                    <div key={product._id} className={`product-card ${selectedProduct === product._id ? 'selected' : ''}`} onClick={() => setSelectedProduct(product._id)}>
                      <div className="product-radio">
                        <input type="radio" name="product" checked={selectedProduct === product._id} onChange={() => setSelectedProduct(product._id)} />
                      </div>
                      {product.image && (
                        <img src={product.image} alt={product.name} className="product-image" onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <div className="product-info">
                        <p className="product-name">{product.name}</p>
                        {product.description && <p className="product-description">{product.description.substring(0, 60)}...</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="swap-note">
                ⚠️ <strong>Note:</strong> You can swap products unlimited times, but only for deliveries scheduled at least 1 day in advance.
              </div>
            </div>
            <div className="swap-modal-footer">
              <button className="cancel-btn" onClick={closeSwapModal} disabled={swapping}>Cancel</button>
              <button className="save-btn" onClick={handleSwapProduct} disabled={swapping || !selectedProduct || selectedProduct === swapModal.delivery[swapModal.productType]}>
                {swapping ? 'Swapping...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSubscriptionDashboard;